import styles from "./Ai.module.scss"
import clsx from "clsx"
import SendIcon from "@mui/icons-material/Send"
import { TextareaAutosize } from "@mui/material"
import { useEffect, useState, useRef, forwardRef, useMemo } from "react"
import "src/pages/building/equipment/AIReport/styles.scss"
import ChatMessagesList from "./ChatMessagesList"
import AiResponseRenderer from "./ai-response-renderer"
import { aiChatWebSocketURL } from "src/aws-exports"
import { useSelector } from "react-redux"
import { v4 as uuidv4 } from "uuid"
import { useQuery, useMutation } from "src/hooks/APIHooks"
import { getEquipmentDetailsById, getRDRReport, getChartData } from "./graphql"
import {
  selectAnomalyUoMUnits,
  selectAnomalyEquipmentTypes
} from "src/redux/slicers/buildingSummarySlice"
import { fectchAccessToken } from "src/utils/CommonMethods"
import moment from "moment"
import momentTimeZone from "moment-timezone"
import { selectUserInfo, getSession } from "src/redux/slicers/userData"
import {
  formatRDRDataToTableData,
  formatToChartData,
  getSpecialSubStringByChar,
  getCharDataRequestFormat
} from "src/pages/fault-detection/utils"
import PrePromptsModal from "./PrePromptsModal"
import DisclaimerModal from "./DisclaimerModal"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import useApiDigitalInspectionReport from "src/sideEffects/useApiDigitialInspectionReport"
export interface PromptButton {
  title: string
  prompt: string
  reportType: string
  type?: string
  linkType?: string
  disabled?: boolean
}

export const promptButtons: PromptButton[] = [
  {
    title: "Time series analysis",
    reportType: "timeseries",
    prompt: `Please provide the detailed timeseries analysis report, which includes:
        1. Issues found.
        2. Insights.
        3. Recommendations.
        4. Next Steps.
        5. Provide five potential prompts as descriptions in a table with links (right side) in the title for further investigation based on your analysis to make sure the equipment is functioning properly.`
  },
  {
    title: "Analytics analysis",
    reportType: "analytics",
    prompt: `Please analyze analytics results and provide a detailed report that includes:
        1. Issues or Anomalies found
        2. Insights.
        3. Recommendations.
        4. Provide five potential prompts as descriptions in a table with links (right side) in the title for further investigation based on your analysis to make sure the equipment is functioning properly.`
  },
  {
    title: "Exception summary",
    reportType: "exceptions",
    prompt: `Please analyze the exception results and provide
        1. Issues found.
        2. Insights.
        3. Recommendations.
        4. Provide five potential prompts as descriptions in a table with links (right side) in the title for further investigation based on your analysis to make sure the equipment is functioning properly.`
  },
  {
    title: "Service Advisory",
    reportType: "advisories",
    prompt: `Please analyze service advisories and provide:
        1. Summary.
        2. Table with exceptions.
        3. Recommendations: Color code important points.
        4. Provide five potential prompts as descriptions in a table with links (right side) in the title for further investigation based on your analysis to make sure the equipment is functioning properly.`
  },
  {
    title: "Digital Inspection",
    reportType: "di_report",
    prompt: `Can you please provide:
        1. A comprehensive summary of each system category including  its 'Success %' , 'Automated Tests' names, 'Total Count', 'Success Count', 'Failure Count'  in a table format
        2. Highlight any concerning areas where success rates are below 90%
        3. Identify specific tests with high failure rates
        4. Provide recommendations for maintenance or further investigation
        5. Create an executive summary of the overall system health
        6. Create an summary of each system category, highlight the name`,
    disabled: true
  },
  {
    title: "Example Prompts",
    reportType: "",
    prompt: "",
    type: "link",
    linkType: "prompts"
  }
  //{ title: 'Disclaimer', reportType: '', prompt: '', type: 'link', linkType: 'disclaimer' },
]

export const getReportTitle = (reportType) => {
  return promptButtons?.find((x) => x?.reportType === reportType)?.title || ""
}

// @ts-ignore
const ChatMessagesArea = ({
  reportDetails,
  theme,
  diTab,
  printableMessagesHandler,
  validDates = true
}) => {
  const [showPromptsModal, setShowPromptsModal] = useState(false)

  const [showDisclaimer, setShowDisclaimer] = useState(false)

  const searchParams: any = new URLSearchParams(location.search)

  const { equipmentTypes } = useSelector(selectAnomalyEquipmentTypes)

  const { uomUnits } = useSelector(selectAnomalyUoMUnits)

  const [messages, setMessages] = useState([])

  const [isConnected, setIsConnected] = useState(false)

  const [botLoading, setBotLoading] = useState(true)

  const wsRef = useRef(null)

  const [selectedResponseForPrint, setSelectedResponseForPrint] = useState([])

  const reportTypeRef = useRef("timeseries")

  const [text, setText] = useState(
    promptButtons?.find((x) => x?.reportType === "timeseries")?.prompt || ""
  )

  const [lastMessage, setLastMessage] = useState("")

  // We use this only RDR and Graph data comes from our own API
  const [apiDataLoadingMessage, setAPIDataLoadingMessage] = useState("")

  const [reportType, setReportType] = useState("timeseries")

  reportTypeRef.current = reportType

  // we will romove some

  const messageBuffer = useRef("")

  const messageEndRef = useRef(null)

  const specialRequestType = useRef("")

  const equipmentDetailsRef = useRef(null)

  const userInfo = useSelector(selectUserInfo)

  const togglePromptModal = () => {
    setShowPromptsModal(!showPromptsModal)
  }

  const toggleDisclaimerModal = () => {
    setShowDisclaimer(!showDisclaimer)
  }

  const pdfCheckboxHandler = (id) => {
    if (selectedResponseForPrint?.includes(id)) {
      setSelectedResponseForPrint(
        selectedResponseForPrint.filter((item) => item !== id)
      )
    } else {
      setSelectedResponseForPrint([...selectedResponseForPrint, id])
    }
  }

  const setErrorMessageInChat = (message?) => {
    setMessages((messages) => [
      ...messages,
      {
        actor: "ai",
        message: message
          ? message
          : "Sorry, I am unable find the data for the specified date range"
      }
    ])
    // when in error, reset all, just in case
    setBotLoading(false)
    specialRequestType.current = ""
    setAPIDataLoadingMessage("")
    messageBuffer.current = ""
    setLastMessage("")
  }

  useEffect(() => {
    const printMessages = messages?.filter((item) =>
      selectedResponseForPrint?.includes(item?.id)
    )
    printableMessagesHandler?.(printMessages)
  }, [selectedResponseForPrint])

  useEffect(() => {
    messageEndRef.current.scrollIntoView({
      behavior: "smooth",
      block: "end",
      align: false
    })
  }, [text, lastMessage])

  useEffect(() => {
    //  added a timeout, so that messages will be fully rendered, before it starts scrolling
    messages?.length > 0 &&
      setTimeout(() => {
        messageEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          align: false
        })
      }, 100)
  }, [messages])

  const { refetch: getMoreEquipmentDetatails } = useQuery({
    query: getEquipmentDetailsById,
    onSuccess: async (data) => {
      if (data) {
        equipmentDetailsRef.current = data
      }
    },
    disableInitialLoad: true,
    dataPath: "data.getEquipment"
  })

  const { onSubmit: getRDRReportQuery } = useMutation({
    query: getRDRReport,
    onSuccess: async (data) => {
      try {
        if (data?.data?.generateEHRDataReport) {
          const apiRes = JSON.parse(data?.data?.generateEHRDataReport)
          if (specialRequestType?.current === "rdr") {
            if (apiRes?.body) {
              const formattedHTMLTable = formatRDRDataToTableData(
                apiRes?.body,
                reportDetails?.equipmentName
              )
              setMessages((messages) => [
                ...messages,
                {
                  actor: "ai",
                  message: formattedHTMLTable || "",
                  isMarkdown: true,
                  isExpanded: false,
                  id: uuidv4(),
                  reportTitle: "RDR Data"
                }
              ])
            } else {
              setErrorMessageInChat()
            }
          } else {
            const chartDataFormat =
              apiRes?.body &&
              formatToChartData(
                apiRes?.body,
                reportDetails?.equipment_type,
                reportDetails?.equipment_id,
                equipmentTypes[reportDetails?.equipment_type],
                uomUnits
              )
            if (chartDataFormat) {
              setMessages((messages) => [
                ...messages,
                {
                  actor: "ai",
                  message: "chartDate",
                  componentType: "customChart",
                  chartProps: chartDataFormat,
                  isMarkdown: true,
                  isExpanded: false,
                  id: uuidv4(),
                  reportTitle: "Chart"
                }
              ])
            } else {
              setErrorMessageInChat()
            }
          }
          specialRequestType.current = ""
        }
      } catch (e) {
        setErrorMessageInChat()
      }
    },
    onError: (error) => {
      setErrorMessageInChat()
    }
  })

  const { refetch: getChartDataQuery } = useQuery({
    query: getChartData,
    onSuccess: async (data) => {
      try {
        if (data) {
        }
      } catch (e) {
        setErrorMessageInChat()
      }
    },
    onError: (error) => {
      setErrorMessageInChat()
    },
    disableInitialLoad: true,
    dataPath: "data.getEquipment"
  })

  const getPropertiesChartData = async (parametersList) => {
    const chartRequest = getCharDataRequestFormat({
      ...reportDetails,
      propertyKeys: parametersList
    })
    await getChartDataQuery(chartRequest)
  }

  const getPropertiesRawData = async (parameterList, startDate, endDate) => {
    if (equipmentDetailsRef?.current?.deviceId) {
      await getRDRReportQuery({
        requestBody: JSON.stringify({
          startDate: startDate,
          endDate: endDate,
          format: "json",
          deviceSerial: {
            [equipmentDetailsRef.current?.deviceId]: {
              // @ts-ignore
              [reportDetails?.equipment_id]: reportDetails?.equipmentName
            }
          },
          parameterList,
          characteristicList: [],
          isWeather: 0,
          isNonStandard: 0,
          isOutOfRange: 1,
          //@ts-ignore
          buildingId: {
            [reportDetails?.building_id]: reportDetails?.buildingName
          },
          // @ts-ignore
          equipment: {
            [reportDetails?.equipment_type]: reportDetails?.equipment_type
          },
          langDir: "en-US",
          localTime: equipmentDetailsRef?.current?.building?.tz
            ? momentTimeZone()
                .locale("en")
                .tz(equipmentDetailsRef?.current?.building?.tz)
                ?.format("LLLL")
            : momentTimeZone().locale("en").tz("UTC")?.format("LLLL"),
          report: "rdr",
          systemOfMeasure: "IP",
          language: "en"
        })
      })
    } else {
      setErrorMessageInChat()
    }
  }

  //DigitalInspection prompt Related
  const {
    data: DIresponseInApiFormat,
    isApiLoading: isDIReportDetailsLoading
  } = useApiDigitalInspectionReport({
    buildingId: reportDetails?.building_id,
    equipment: reportDetails?.equipment_id,
    systemOrEquipmentType: reportDetails?.equipment_type,
    startDate: diTab
      ? moment(searchParams.get("startDate")).format("YYYY-MM-DD")
      : reportDetails?.start_date,
    endDate: diTab
      ? moment(searchParams.get("endDate")).format("YYYY-MM-DD")
      : reportDetails?.end_date,
    barScale: 1,
    checkedAts: [],
    enabled:
      (
        reportDetails?.offeringCodes ||
        equipmentDetailsRef?.current?.building?.offeringCodes
      )?.length > 0
        ? true
        : false,
    offeringCodes:
      reportDetails?.offeringCodes ||
      equipmentDetailsRef?.current?.building?.offeringCodes
  })

  const updatedPromptButtons: PromptButton[] = useMemo(() => {
    if (DIresponseInApiFormat) {
      const updatedButtons = promptButtons.map((item) => {
        if (item?.title === "Digital Inspection") {
          return { ...item, disabled: isDIReportDetailsLoading }
        }
        return item
      })
      return updatedButtons
    } else {
      return promptButtons
    }
  }, [isDIReportDetailsLoading, DIresponseInApiFormat])

  const normalLLMFlow = (data) => {
    if (data?.includes("COMMAND COMPLETED")) {
      setLastMessage("")
      setBotLoading(false)
      const fullMessage = messageBuffer.current
      // @ts-ignore
      const reportType = reportTypeRef?.current
      setMessages((messages) => [
        ...messages,
        {
          actor: "ai",
          message: fullMessage || "",
          isMarkdown: true,
          isExpanded: true,
          id: uuidv4(),
          reportTitle: getReportTitle(reportType)
        }
      ])
      messageBuffer.current = ""
    } else {
      messageBuffer.current += data
      setLastMessage(messageBuffer.current)
    }
  }

  function isJson(str) {
    try {
      JSON.parse(str)
    } catch (e) {
      return false
    }
    return true
  }

  const getRDRParameterList = (data) => {
    const jsonString = getSpecialSubStringByChar("{", "}", data)
    const resultData = JSON.parse(jsonString)
    let propertyKeys = []
    const parameterList = resultData?.requestBody.parameterList || []
    // const startDate = resultData?.requestBody?.startDate
    // const endDate = resultData?.requestBody?.endDate
    let startDate = reportDetails?.start_date
    let endDate = reportDetails?.end_date
    if (diTab) {
      startDate = moment(searchParams.get("startDate")).format("YYYY-MM-DD")
      endDate = moment(searchParams.get("endDate")).format("YYYY-MM-DD")
    }
    if (parameterList?.[0]?.includes(" ")) {
      // sometime llm returns tisDisplayname instead of target keys
      // so we are replacing names with keys
      parameterList.forEach((item) => {
        const propertyDetails =
          equipmentTypes[reportDetails?.equipment_type]?.find(
            (x) => x?.targetKey === item || x?.tisDisplayName === item
          ) || {}
        if (propertyDetails?.targetKey) {
          propertyKeys.push(propertyDetails?.targetKey)
        }
      })
    } else {
      propertyKeys = parameterList
    }
    return {
      startDate,
      endDate,
      propertyKeys
    }
  }

  const specialAPIFlow = async (data) => {
    try {
      if (data?.includes("COMMAND COMPLETED")) {
        const fullMessage = messageBuffer.current

        console.log(fullMessage)
        // @ts-ignore
        const { startDate, endDate, propertyKeys } =
          getRDRParameterList(fullMessage)
        if (propertyKeys?.length > 0 && startDate && endDate) {
          if (specialRequestType?.current === "rdr") {
            await getPropertiesRawData(propertyKeys, startDate, endDate)
          } else {
            // having problem with chart data as of now, so we are using RDR API
            // we may revisit to use chart data API in future
            // await getPropertiesChartData(propertiesList)
            await getPropertiesRawData(propertyKeys, startDate, endDate)
          }
        } else {
          setErrorMessageInChat()
        }
        setLastMessage("")
        setAPIDataLoadingMessage("")
        setBotLoading(false)
        messageBuffer.current = ""
      } else {
        messageBuffer.current += data
        setLastMessage(messageBuffer.current)
      }
    } catch (e) {
      console.log("error", e)
      setErrorMessageInChat()
    }
  }

  const connectWebSocket = async () => {
    if (wsRef.current) return // Prevent duplicate connections
    const token = await getSession(true)
    const urlWithAuth = `${aiChatWebSocketURL}?Auth=${token}&&bId=${reportDetails.building_id}&&aId=${reportDetails.aId}&&sId=${reportDetails.sId}`
    // @ts-ignore
    const ws = new WebSocket(urlWithAuth)

    ws.onopen = () => {
      console.log("WebSocket connected")
      setIsConnected(true)
    }

    ws.onmessage = (event) => {
      try {
        if (
          event?.data?.includes("pong") ||
          event?.data?.includes("server error") ||
          event?.data?.includes("request timed out")
        ) {
          // if just keeps the ws connection alive [ Ping and Pong]
          return
        }
        if (specialRequestType?.current === "") {
          normalLLMFlow(event?.data)
        } else {
          specialAPIFlow(event?.data)
        }
      } catch (e) {
        setErrorMessageInChat()
      }
    }

    ws.onclose = () => {
      console.log("WebSocket disconnected")
      setIsConnected(false)
      wsRef.current = null
    }

    ws.onerror = (error) => {
      setErrorMessageInChat("You are not authorized to access this data")
    }

    wsRef.current = ws
  }

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close()
    }
  }

  const sendMessage = (request) => {
    if (wsRef?.current && isConnected) {
      wsRef.current?.send(JSON.stringify(request))
    }
  }

  const initializedWebSocket = async () => {
    try {
      setInterval(() => {
        if (isConnected) {
          sendMessage({ message: "ping", action: "ping" })
        }
      }, 20000)
    } catch (e) {}
  }

  useEffect(() => {
    return () => {
      // close websocket once chat window is closed
      disconnectWebSocket()
    }
  }, [])

  const getRDRRequestPrompt = (inputText) => {
    const requestJSONFormat = "Look at the below JSON and replace the parameterList with 'targetKey' from anomalies/issues found.Return only the new JSON {\"requestBody\": {\"startDate\":\"2024-12-07\",\"endDate\":\"2024-12-07\",\"format\":\"json\",\"deviceSerial\":{\"DID\":{\"EQUID\":\"Chiller-1\"}},\"parameterList\":[\"PARAM1\"],\"characteristicList\":[],\"isWeather\":0,\"isNonStandard\":0,\"isRDR\":1,\"buildingId\":{\"BID\":\"BNAME\"},\"equipment\":{\"EQTYPE\":\"EQTYPE\"},\"langDir\":\"en-US\",\"localTime\":\"Wednesday, April 9, 2025 11:03 AM\",\"report\":\"rdr\",\"systemOfMeasure\":\"IP\",\"language\":\"en\"}}"
    return requestJSONFormat
  }

  const getChartRequestPrompt = (inputText) => {
    const requestJSONFormat = "Look at the below JSON and replace the parameterList with 'targetKey' from anomalies/issues found.Return only the new JSON {\"requestBody\": {\"startDate\":\"2024-12-07\",\"endDate\":\"2024-12-07\",\"format\":\"json\",\"deviceSerial\":{\"DID\":{\"EQUID\":\"Chiller-1\"}},\"parameterList\":[\"PARAM1\"],\"characteristicList\":[],\"isWeather\":0,\"isNonStandard\":0,\"isRDR\":1,\"buildingId\":{\"BID\":\"BNAME\"},\"equipment\":{\"EQTYPE\":\"EQTYPE\"},\"langDir\":\"en-US\",\"localTime\":\"Wednesday, April 9, 2025 11:03 AM\",\"report\":\"rdr\",\"systemOfMeasure\":\"IP\",\"language\":\"en\"}}"
    return requestJSONFormat
  }

  const getPromptText = (inputText) => {
    if (
      inputText?.toLowerCase()?.includes("rdr") ||
      inputText?.toLowerCase().includes("raw data")
    ) {
      specialRequestType.current = "rdr"
      setAPIDataLoadingMessage("Generating RDR Data...")
      return getRDRRequestPrompt(inputText)
    } else if (
      inputText?.toLowerCase()?.includes("graph") ||
      inputText?.toLowerCase().includes("plot") ||
      inputText?.toLowerCase().includes("chart")
    ) {
      specialRequestType.current = "chart"
      setAPIDataLoadingMessage("Generating Chart Data...")
      return getChartRequestPrompt(inputText)
    } else {
      return inputText
    }
  }

  const triggerLLMBot = async (inputText, reportTypeFromExample?: string) => {
    try {
      if (inputText) {
        setBotLoading(true)
        const requestData = {
          building_id: reportDetails?.building_id,
          equipment_id: reportDetails?.equipment_id,
          equipment_type: reportDetails?.equipment_type,
          ...(diTab
            ? {
                reportDate: moment(searchParams.get("startDate")).format(
                  "YYYY-MM-DD"
                ),
                start_date: moment(searchParams.get("startDate")).format(
                  "YYYY-MM-DD"
                ),
                end_date: moment(searchParams.get("endDate")).format(
                  "YYYY-MM-DD"
                )
              }
            : {
                reportDate: reportDetails?.report_date,
                start_date: reportDetails?.start_date,
                end_date: reportDetails?.end_date
              }),
          report_type: reportTypeFromExample || reportType,
          user_prompt: getPromptText(inputText),
          ...(reportType === "timeseries"
            ? {
                pid_thresholds: {
                  "81": 2.5,
                  "272": 3.5,
                  "467": 2,
                  "1026": 4
                }
              }
            : {}),
          ...(reportType === "di_report"
            ? {
                data: JSON.stringify(DIresponseInApiFormat)
              }
            : {}),
          userData: {
            fullName: `${userInfo?.firstName || ""} ${
              userInfo?.lastName || ""
            }`,
            role: "technician"
          }
        }
        sendMessage(requestData)
      }
    } catch (e) {
      setErrorMessageInChat()
      console.log(e)
    }
  }

  useEffect(() => {
    // call once websocket initialzed
    if (isConnected) {
      initializedWebSocket()
      setBotLoading(false)
    }
  }, [isConnected])

  const initializeData = async () => {
    try {
      if (reportDetails?.building_id && reportDetails?.equipment_id) {
        await fectchAccessToken(reportDetails?.building_id)
        // @ts-ignore
        connectWebSocket()
        reportDetails?.equipment_id &&
          getMoreEquipmentDetatails({
            equipmentId: reportDetails?.equipment_id
          })
      }
    } catch (error) {}
  }

  useEffect(() => {
    initializeData()
  }, [reportDetails])

  const sendButtonHandler = (
    reportType?: string,
    predefinedPrompt?: string
  ) => {
    // @ts-ignore
    const inputText = document.getElementById("ai-bot-text-area")?.value
    if (inputText && !botLoading && validDates) {
      setMessages((messages) => [
        ...messages,
        { actor: "user", message: inputText }
      ])
      triggerLLMBot(predefinedPrompt || inputText, reportType)
      setText("")
    }
  }

  const preDefinedPromptHanlderClick = (reportType, prompt) => {
    setReportType(reportType)
    setText(prompt)
    setShowPromptsModal(false)
    setTimeout(() => {
      sendButtonHandler(reportType, prompt)
    }, 300)
  }

  const onClickHandler = (event) => {
    try {
      if (event.target.tagName.toLowerCase() === "a") {
        const parentRow = event?.target.parentElement?.parentElement
        if (parentRow?.tagName?.toLowerCase() === "tr") {
          const prompt = parentRow?.childNodes?.[0].innerHTML
          if (typeof prompt === "string") {
            setText(prompt)
            setTimeout(() => {
              sendButtonHandler()
            }, 100)
          }
        }
        event?.preventDefault()
      }
    } catch (error) {
      console.log(error)
    }
  }

  const getContainerHeight = () => {
    const container = document.getElementById("ai-bot-text-area")
    const presentHeight = (93 * window.innerHeight) / 100
    const newHeight = presentHeight - (container?.clientHeight + 225)
    return newHeight
  }

  const [messageContainerHeight, setMessageContainerHeight] = useState(300)

  useEffect(() => {
    const newHeight = getContainerHeight()
    setMessageContainerHeight(newHeight)
  }, [text])

  const handleKeyPress = (e) => {
    // disabling enter key submit for now
    // @ts-ignore
    if (
      (e.key === "Enter" || e.keyCode === 13) &&
      e.ctrlKey &&
      // @ts-ignore
      text?.value !== ""
    ) {
      sendButtonHandler()
    }
  }

  const formatPromtText = (promt) => {
    const formattedText = promt
      .replace("$date", reportDetails?.report_date)
      ?.replace("$buildingId", reportDetails?.building_id)
      ?.replace("$equipmentId", reportDetails?.equipment_id)
      ?.replace("$equipmentType", reportDetails?.equipment_type)
    return formattedText
  }

  return (
    <>
      <div
        style={{ height: `${messageContainerHeight}px` }}
        className={clsx(styles.messagescontainer)}
      >
        <ChatMessagesList
          onClickHandler={onClickHandler}
          messages={messages}
          theme={theme}
          pdfCheckboxHandler={pdfCheckboxHandler}
          reportDetails={reportDetails}
        />
        {(apiDataLoadingMessage || lastMessage) && (
          <AiResponseRenderer
            onClickHandler={onClickHandler}
            pdfCheckboxHandler={pdfCheckboxHandler}
            reportDetails={reportDetails}
            data={{
              message: apiDataLoadingMessage || lastMessage,
              reportTitle: getReportTitle(reportType)
            }}
            theme={theme}
          />
        )}

        <div ref={messageEndRef} />
        {botLoading && (
          <div className={clsx(styles.aimodalmessageline)}>
            <div className={clsx(styles.loadingmessage, styles.aimodalmessage)}>
              <span>...</span>
            </div>
          </div>
        )}
      </div>
      <footer>
        <p style={{ margin: "2px 2px 2px 10px", color: "#6f6e6d" }}>
          You can use the preconfigured prompts, click on any of the buttons
          down below to get the specific reports,{" "}
          <span style={{ color: "orange" }}>
            {" "}
            you can either click Send button or press Control+Enter.
          </span>
          {diTab && (
            <span>
              {" "}
              Please reduce the dates to 1 day, to get more accurate results.
            </span>
          )}
        </p>
        <div className="ai-prompt-buttons-container">
          {updatedPromptButtons?.map((x) => {
            // @ts-ignore
            if (x?.type === "link") {
              return (
                <div
                  className={"ai-prompt-sample-link"}
                  onClick={() => {
                    if (x?.linkType === "prompts") {
                      setShowPromptsModal(true)
                    } else {
                      setShowDisclaimer(true)
                    }
                  }}
                  key={x?.title}
                >
                  {x?.title}
                </div>
              )
            } else {
              return (
                <div
                  className={`ai-prompt-button ${
                    reportType === x?.reportType ? "selected" : ""
                  } ${x?.disabled === true ? "disabled" : ""}`}
                  onClick={() => {
                    setText(x?.prompt)
                    setReportType(x?.reportType)
                  }}
                  key={x?.title}
                >
                  {reportType === x?.reportType && (
                    <span className="icon-checkmark2"></span>
                  )}
                  {x?.title}
                </div>
              )
            }
          })}
        </div>
        <span
          style={{
            color: "black",
            fontWeight: 600,
            margin: "10px",
            textDecoration: "italic"
          }}
        >
          <span style={{ fontSize: 12 }}>
            <ErrorOutlineIcon />
          </span>{" "}
          <i>
            You are interacting with an AI-powered chatbot that helps provide
            information in response to your diagnostic questions, and it is
            important to use your human judgment before acting upon the
            information provided.
          </i>
        </span>
        <TextareaAutosize
          id="ai-bot-text-area"
          maxRows={7}
          minRows={1}
          placeholder="Type your question here..."
          onKeyDown={handleKeyPress}
          value={text}
          onChange={(e) => {
            setText(e.target.value)
          }}
        />
        <div
          className={clsx(styles.sendbutton, {
            [styles.disabled]: botLoading || !validDates
          })}
          onClick={() => sendButtonHandler()}
        >
          <span>SEND</span>
          <span>
            <SendIcon style={{ width: 15, height: 13, marginLeft: 5 }} />
          </span>
        </div>
        {showPromptsModal && (
          <PrePromptsModal
            preDefinedPromptHanlderClick={preDefinedPromptHanlderClick}
            togglePromptModal={togglePromptModal}
          />
        )}
        {/* {showDisclaimer && <DisclaimerModal toggleDisclaimerModal={toggleDisclaimerModal} />} */}
      </footer>
    </>
  )
}

export default ChatMessagesArea
